import React, { useState, useMemo } from 'react';
import { Box, Typography, Button, Grid, CircularProgress } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useInventory } from '../../../contexts/InventoryContext';
import { SearchBar, DeveloperCard } from '../projects';

/**
 * Developers tab content. Uses context + SearchBar, DeveloperCard with menu (Edit, Add Project, Delete).
 */
export default function DeveloperList({
  onAddDeveloper,
  onOpenProjects,
  onEditDeveloper,
  onAddProjectForDeveloper,
  onDeleteDeveloper,
}) {
  const { developers, developersLoading, projects } = useInventory();
  const [search, setSearch] = useState('');

  const projectCountByDeveloperId = useMemo(() => {
    const map = {};
    (projects || []).forEach((p) => {
      const id = p.developer_id ?? p.developer?.id ?? p.developer;
      if (id != null) map[Number(id)] = (map[Number(id)] || 0) + 1;
    });
    return map;
  }, [projects]);

  const availableCountByDeveloperId = useMemo(() => {
    const map = {};
    (projects || []).forEach((p) => {
      const id = p.developer_id ?? p.developer?.id ?? p.developer;
      if (id != null) {
        const avail = p.available ?? p.unit_stats?.available ?? 0;
        map[Number(id)] = (map[Number(id)] || 0) + (Number(avail) || 0);
      }
    });
    return map;
  }, [projects]);

  const filtered = useMemo(() => {
    if (!search.trim()) return developers;
    const q = search.toLowerCase();
    return developers.filter((d) => (d.name || '').toLowerCase().includes(q));
  }, [developers, search]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">{developers.length} developers · manage builder relationships</Typography>
        </Box>
        <Button size="medium" variant="contained" startIcon={<AddIcon />} onClick={onAddDeveloper}>
          Add Developer
        </Button>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search developers..." />
      </Box>
      {developersLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} sx={{ color: 'success.main' }} />
        </Box>
      ) : (
        <Grid container spacing={1.5} sx={{ alignItems: 'stretch' }}>
          {filtered.map((d) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={d.id} sx={{ display: 'flex' }}>
              <DeveloperCard
                developer={d}
                projectCount={projectCountByDeveloperId[d.id] ?? 0}
                availableCount={availableCountByDeveloperId[d.id] ?? 0}
                onEdit={onEditDeveloper}
                onAddProject={onAddProjectForDeveloper}
                onDelete={onDeleteDeveloper}
                onOpenProjects={onOpenProjects}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
